/**
 * Server state of the admin trial-invite page: the brand's invite list and
 * the create / revoke mutations (which refresh the list).
 *
 * @see docs/specifications/2026-09-30-trial-invite-bez-proformy/spec.md
 */
import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createTrialInvite,
  listTrialInvites,
  revokeTrialInvite,
  RevokeTrialInviteError,
  type CreateTrialInvitePayload,
  type TrialInvite,
} from "../../../services/trialInviteApi";
import { extractErrorMessage } from "../../../utils/extractErrorMessage";

export const trialInvitesKey = (brandId: string | null) =>
  ["admin", "trial-invites", brandId] as const;

export interface RevokeFailure {
  message: string;
  /** False for 409/404 — repeating the request cannot succeed. */
  retryable: boolean;
}

const toRevokeFailure = (error: unknown): RevokeFailure =>
  error instanceof RevokeTrialInviteError
    ? { message: error.message, retryable: error.reason === "failed" }
    : { message: "Nie udało się odwołać zaproszenia.", retryable: true };

export function useTrialInvites(brandId: string | null) {
  const queryClient = useQueryClient();

  const invitesQuery = useQuery({
    queryKey: trialInvitesKey(brandId),
    queryFn: brandId ? () => listTrialInvites(brandId) : skipToken,
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateTrialInvitePayload) =>
      createTrialInvite(payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: trialInvitesKey(brandId) }),
  });

  const revokeMutation = useMutation({
    mutationFn: (invite: TrialInvite) => revokeTrialInvite(invite.id),
    // A 409/404 also means the list is stale, so refresh after any outcome.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: trialInvitesKey(brandId) }),
  });

  return {
    invitesQuery,
    createMutation,
    revokeMutation,
    revokeFailure: revokeMutation.isError
      ? toRevokeFailure(revokeMutation.error)
      : null,
    createError: createMutation.isError
      ? extractErrorMessage(
          createMutation.error,
          "Nie udało się utworzyć zaproszenia.",
        )
      : null,
  };
}
